import {
  ErcotAuthorizationTerms,
  ErcotDisclosureTerms,
} from "@Light/components/enroll/terms/ErcotTerms";
import { PaAuthorizationTerms } from "@Light/components/enroll/terms/PaAuthorizationTerms";
import { Hyperlink } from "@Light/components/page/Hyperlink";
import TexasIcon from "@Light/icons/TexasIcon";
import {
  type PlanResponse,
  type Requirements,
  UtilityIdentifier,
} from "@Light/services/lightTypes";
import { Home02 } from "@untitledui/icons";
import type { SVGProps } from "react";
import pecoExample from "../../../public/images/examples/peco.jpg";
import pplExample from "../../../public/images/examples/ppl.jpg";

export type TrustDisplay = {
  icon: React.FC<SVGProps<SVGSVGElement>>;
  text: string;
  license?: string;
};

export type EnrollmentTermsProps = {
  plan: PlanResponse;
};

export type UtilityBehavior = {
  /** How a utility's address and account number should be looked up. */
  serviceLocationEntry: "search" | "manual";
  /** Whether or not to show the service event field (when not required). */
  needsServiceEvent: boolean;
  /** Whether or not to show the date of birth field (when not required). */
  needsDateOfBirth: boolean;
  /** How the utility refers to utility account numbers. */
  accountNumberName: string;
  /**
   * Whether the customer must explicitly confirm the utility even when it's
   * the only utility we support in the zip.
   */
  confirmFromZip: boolean;
  /** Description of how to find the utility account number. */
  accountNumberDescription?: React.ReactNode;
  /**
   * Array of terms the customer must accept to complete enrollment, each rendered as
   * its own checkbox.
   */
  enrollmentTerms: React.FC<EnrollmentTermsProps>[];
  /** Trust badge shown under the "Powered by Light" attribution. */
  trustDisplay: TrustDisplay;
};

/** Fallback behavior for unknown utility. */
export const DEFAULT_UTILITY_BEHAVIOR: UtilityBehavior = {
  accountNumberName: "Account number",
  // Fallback to search for unknown utilities. Their search will only match up
  // to known addresses from known utilities.
  serviceLocationEntry: "search",
  needsServiceEvent: true,
  needsDateOfBirth: true,
  confirmFromZip: false,
  enrollmentTerms: [],
  trustDisplay: { icon: Home02, text: "Trusted by thousands of neighbors" },
};

const ERCOT: UtilityBehavior = {
  ...DEFAULT_UTILITY_BEHAVIOR,
  accountNumberName: "ESI ID",
  serviceLocationEntry: "search",
  enrollmentTerms: [ErcotDisclosureTerms, ErcotAuthorizationTerms],
  trustDisplay: {
    icon: TexasIcon,
    text: "Trusted by thousands of Texans",
    license: "PUCT #10340",
  },
};

const PJM: UtilityBehavior = {
  ...DEFAULT_UTILITY_BEHAVIOR,
  serviceLocationEntry: "manual",
  needsServiceEvent: false,
  needsDateOfBirth: false,
  confirmFromZip: true,
  enrollmentTerms: [PaAuthorizationTerms],
  trustDisplay: {
    ...DEFAULT_UTILITY_BEHAVIOR.trustDisplay,
    license: "PA PUC #A-2026-3062117",
  },
};

const UTILITY_BEHAVIOR: Record<UtilityIdentifier, UtilityBehavior> = {
  [UtilityIdentifier.AepCentral]: ERCOT,
  [UtilityIdentifier.AepNorth]: ERCOT,
  [UtilityIdentifier.Centerpoint]: ERCOT,
  [UtilityIdentifier.Lubbock]: ERCOT,
  [UtilityIdentifier.Oncor]: ERCOT,
  [UtilityIdentifier.Tnmp]: ERCOT,
  [UtilityIdentifier.Peco]: {
    ...PJM,
    accountNumberName: "Electric Choice ID",
    accountNumberDescription: (
      <>
        <div>
          Your Electric Choice ID is a 10-digit number that can be found on the
          second page of your PECO bill (pictured below) or by logging into your{" "}
          <Hyperlink
            href="https://secure.peco.com/Pages/Login.aspx"
            target="_blank"
          >
            PECO account
          </Hyperlink>
          .
        </div>
        <img
          className="mt-4 rounded-2xl"
          src={pecoExample}
          alt="PECO bill example"
        />
      </>
    ),
  },
  [UtilityIdentifier.Ppl]: {
    ...PJM,
    accountNumberDescription: (
      <>
        <div>
          Your account number is a 10-digit number that can be found on the
          top-right of your PPL bill (pictured below) or by logging into your{" "}
          <Hyperlink
            href="https://selfserve.pplelectric.com/sign-in"
            target="_blank"
          >
            PPL account
          </Hyperlink>
          .
        </div>
        <img
          className="mt-4 rounded-2xl"
          src={pplExample}
          alt="PPL bill example"
        />
      </>
    ),
  },
};

export function getUtilityBehavior(
  utility: UtilityIdentifier | undefined,
): UtilityBehavior {
  return (utility && UTILITY_BEHAVIOR[utility]) || DEFAULT_UTILITY_BEHAVIOR;
}

export function getUtilityBehaviorFromRequirements(
  requirements: Requirements,
): UtilityBehavior {
  return getUtilityBehavior(requirements.utility?.value?.utility?.id);
}
